import { Button } from '@helvetia/components/Button'
import { Option } from '@helvetia/components/forms/NativeSelect/NativeSelect.types'
import NumberInput from '@helvetia/components/forms/NumberInput'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import AgentService from '../../../core/services/agent.service'
import { LifecycleView } from '../../_components/ConnectComponent'
import { RadioGroup } from '../../_components/RadioGroup'
import { AdobeEventManager } from '../../_utils/adobeAnalytics'
import { MAX_CLAIMS } from '../../_utils/environmentConst.provider'
import { CalculatorStepper } from '../CaculatorStepper'
import { PreviousInsurancesPresenter } from './PreviousInsurances.presenter'
import { validateFieldForm } from './PreviousInsurances.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface PreviousInsurancesValues {
  prevDamages: number
  cancelledPreviousInsurance: number
  numberOfClaims: number
  claimLoses: number
  years: number
}

interface Props {
  presenter: PreviousInsurancesPresenter
  onSubmit: (values: PreviousInsurancesValues) => void
}

interface State {
  initialValues: PreviousInsurancesValues
  isEditing: boolean
  isLoading: boolean
}

export class PreviousInsurancesRaw
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: PreviousInsurancesPresenter
  private YesNoList: Option[]
  private yearsList: Option[]

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.YesNoList = [
      {
        text: props.t('YesNoRadio.no'),
        value: '0',
      },
      {
        text: props.t('YesNoRadio.yes'),
        value: '1',
      },
    ]

    this.yearsList = [
      { text: '1', value: 1 },
      { text: '2', value: 2 },
      { text: '3', value: 3 },
      { text: '4', value: 4 },
      { text: '5', value: 5 },
    ]

    this.state = {
      initialValues: null,
      isEditing: false,
      isLoading: false,
    }
  }

  setInitialValues = (
    initialValues: PreviousInsurancesValues,
    isEditing: boolean,
  ) => {
    this.setState({
      initialValues,
      isEditing,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: PreviousInsurancesValues) =>
    this.presenter.onContinue(values)

  handleClickBack = () => {
    this.presenter.onBackClick()
  }

  handleLinkClick = (values: PreviousInsurancesValues) => this.presenter.saveAndRedirect(values)


  render() {
    const { t } = this.props
    const { isEditing } = this.state

    if (!this.state.initialValues) return <Spinner size="xl" />
    return (
      <>
        <Row>
          <Col tablet={ 12 } desktop={ 4 } verticalGutter>
            <CalculatorStepper step={ 5 } />
          </Col>

          <Col tablet={ 12 } desktop={ 8 }>
            <Row>
              <Text as="p" color="black" size="l">
                { t('step5.title') }
              </Text>
            </Row>

            <Formik
              initialValues={ this.state.initialValues }
              onSubmit={ this.handleClickContinuar }
              validate={ values => validateFieldForm(values) }
              render={ ({
                setFieldValue,
                setFieldTouched,
                values,
                touched,
                errors,
              }) => (
                  <Form>
                    <Row marginTop="xs" marginBottom="xxxs">
                      <Col>
                        <Field
                          component={ RadioGroup }
                          name="prevDamages"
                          defaultSelected={ values.prevDamages.toString() }
                          questionText={ t('step5.previousDamages') }
                          radioList={ this.YesNoList }
                          onChange={ event => {
                            setFieldValue(
                              'prevDamages',
                              Number(event.currentTarget.value),
                            )
                            Boolean(event.currentTarget.value)
                              ? setFieldValue('years', 5)
                              : setFieldValue('years', 0)
                          } }
                        />
                      </Col>
                    </Row>
                    { Boolean(values.prevDamages) && values.prevDamages > 0 && (
                      <>
                        <Row>
                          <Col mobile={ 12 } tablet={ 10 } desktop={ 8 }>
                            <Field
                              component={ NumberInput }
                              name="numberOfClaims"
                              text={ t('step5.numberOfClaims.question') }
                              label={ t('step5.numberOfClaims.label') }
                              onInputChange={ event => {
                                setFieldTouched('numberOfClaims')

                                setFieldValue(
                                  'numberOfClaims',
                                  Number(event.currentTarget.value),
                                )
                              } }
                            />
                            { touched['numberOfClaims'] &&
                              errors['numberOfClaims'] && (
                                <Row marginTop="xs">
                                  <Col>
                                    <Text as="div" color="black" size="s">
                                      { t('step5.numberOfClaims.exceeded', {
                                        maxClaims: MAX_CLAIMS,
                                      }) }
                                      <LinkText
                                        onClick={ () =>
                                          this.handleLinkClick(values)
                                        }
                                        text={ t('step5.cancelLink') }
                                        color="primary"
                                      />
                                    </Text>
                                  </Col>
                                </Row>
                              ) }
                          </Col>
                        </Row>
                        <Row marginTop="s" marginBottom="xxs">
                          <Col mobile={ 12 } tablet={ 10 } desktop={ 8 }>
                            <Field
                              component={ NumberInput }
                              name="claimLoses"
                              text={ t('step5.claimLoses.question') }
                              label={ t('step5.claimLoses.label') }
                              onInputChange={ event => {
                                setFieldTouched('claimLoses')
                                setFieldValue(
                                  'claimLoses',
                                  Number(event.currentTarget.value),
                                )
                              } }
                            />
                            { touched['claimLoses'] && errors['claimLoses'] && (
                              <Row marginTop="xs">
                                <Col>
                                  <Text as="div" color="black" size="s">
                                    { t('step5.claimLoses.exceeded') }
                                    <LinkText
                                      onClick={ () => this.handleLinkClick(values) }
                                      text={ t('step5.cancelLink') }
                                      color="primary"
                                    />
                                  </Text>
                                </Col>
                              </Row>
                            ) }
                          </Col>
                        </Row>
                      </>
                    ) }

                    <Row marginTop="l" marginBottom="xs">
                      <Col>
                        <Field
                          component={ RadioGroup }
                          name="cancelledPreviousInsurance"
                          questionText={ t('step5.cancelledPreviousInsurance') }
                          defaultSelected={ values.cancelledPreviousInsurance.toString() }
                          onChange={ event => {
                            setFieldValue(
                              'cancelledPreviousInsurance',
                              Number(event.currentTarget.value),
                            )
                          } }
                          radioList={ this.YesNoList }
                        />
                      </Col>
                    </Row>

                    { Boolean(values.cancelledPreviousInsurance) &&
                      values.cancelledPreviousInsurance > 0 && (
                        <Row marginTop="xs">
                          <Col>
                            <Text as="div" color="black" size="s">
                              { t('step5.cancelAdvise') }
                            </Text>
                            { !AgentService.isAgentOrBroker() && (
                              <LinkText
                                onClick={ () => this.handleLinkClick(values) }
                                text={ t('step5.cancelLink') }
                                color="primary"
                              />
                            ) }
                          </Col>
                        </Row>
                      ) }

                    <Row verticalGutter marginTop="xs" marginBottom="m">
                      <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                        <Button
                          primary
                          type="submit"
                          disabled={ Boolean(values.cancelledPreviousInsurance) }
                        >
                          { isEditing ? t('buttons.save') : t('buttons.continue') }
                        </Button>
                      </Col>
                      <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                        <Button grey onClick={ this.handleClickBack }>
                          { isEditing ? t('buttons.cancel') : t('buttons.back') }
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                ) }
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const PreviousInsurancesView = withNamespaces()(
  withRouter(PreviousInsurancesRaw),
)
